/**
 * buildContractData.ts — pure fn: ProjectMeta + EstimateTotalsResult + ClientDetails
 * + PaymentSchedule → ContractData.
 *
 * PDF-03 CARDINAL RULE: price fields are DIRECT PASS-THROUGH from EstimateTotalsResult.
 * NO arithmetic, NO roundTo(), NO toFixed(), NO summation in this file.
 * The contract price IS computeEstimateTotals grossTotal — same parity path.
 *
 * SCOPE LINES: pass-through of already-rounded lineTotal from totals.lines.
 * No quantity/price arithmetic — each line's lineTotal is used as-is.
 *
 * VALIDATION: ClientDetails and PaymentSchedule are validated at the boundary
 * via Zod safeParse (CLAUDE.md rule 7). Parsed values fill any missing defaults.
 */

import { ClientDetailsSchema, PaymentScheduleSchema } from '@/shared/validation/schemas';

// ---------------------------------------------------------------------------
// IN-03: Named constants for PL legal defaults (CLAUDE.md rule 9)
// These values appear in legally-binding contracts — change only with legal counsel.
// Marked [ASSUMED/DRAFT] because DOC-03 gate is not yet approved for production.
// ---------------------------------------------------------------------------
const CONTRACT_DEFAULTS = {
  /** PL: minimum statutory warranty under KC art. 556-576. [ASSUMED/DRAFT] */
  warrantyYears: 2,
  /** Common market rate; review with legal counsel before production use. [ASSUMED/DRAFT] */
  penaltyPercentPerDay: 0.1,
  /** Caps per-day penalty accumulation to protect contractor. [ASSUMED/DRAFT] */
  maxPenaltyPercent: 10,
  /** KC art. 647 — reasonable client inspection period in days. [ASSUMED/DRAFT] */
  acceptanceDays: 7,
  /** Standard number of signed copies (one per party). */
  copiesCount: 2,
} as const;
import type { ClientDetails, PaymentSchedule } from '@/shared/validation/schemas';
import type { EstimateTotalsResult } from '@/entities/estimate/lib/computeTotals';
import type { ProjectMeta } from '@/entities/estimate/model/types';
import type { ContractData, ContractType } from '../model/types';

/**
 * Builds a ContractData object pre-filled from estimate outputs and project metadata.
 *
 * @param meta            - Project metadata (contractor info, vatRate, date)
 * @param totals          - Pre-computed from computeEstimateTotals() — price is strict pass-through
 * @param clientDetails   - Client/investor fields (Zod-validated at boundary)
 * @param paymentSchedule - Payment schedule (Zod-validated at boundary)
 * @param contractType    - Defaults to 'kosztorysowe' (Polish unit-price contract)
 */
export function buildContractData(
  meta: ProjectMeta,
  totals: EstimateTotalsResult,
  clientDetails: ClientDetails,
  paymentSchedule: PaymentSchedule,
  contractType: ContractType = 'kosztorysowe',
): ContractData {
  // Zod boundary validation — fills defaults for missing/undefined fields
  const clientResult = ClientDetailsSchema.safeParse(clientDetails);
  const client: ClientDetails = clientResult.success
    ? clientResult.data
    : ClientDetailsSchema.parse({});

  const scheduleResult = PaymentScheduleSchema.safeParse(paymentSchedule);
  const schedule: PaymentSchedule = scheduleResult.success
    ? scheduleResult.data
    : PaymentScheduleSchema.parse({});

  // Scope lines — pass-through of already-rounded lineTotals (PDF-03: no re-round)
  // WR-01 fix: include name so the PDF "Opis pozycji" column shows a human-readable label
  // instead of the internal UUID itemId.
  const scope = totals.lines.map((line) => ({
    itemId: line.itemId,
    name: line.name || line.itemId, // WR-01: fallback to itemId if name empty or absent
    finalQty: line.finalQty,
    priceLabor: line.priceLabor,
    lineTotal: line.lineTotal, // direct pass-through — NEVER re-round
  }));

  return {
    // Contractor fields from ProjectMeta
    contractorName: meta.contractorName ?? '',
    contractorAddress: meta.contractorAddress ?? '',
    contractorNip: meta.contractorNip ?? '',
    contractorPhone: meta.contractorPhone ?? '',

    // Client
    client,

    // Price pass-through — CRITICAL: no arithmetic below (PDF-03)
    netTotal: totals.netTotal,
    vatAmount: totals.vatAmount,
    grossTotal: totals.grossTotal,
    vatRate: meta.vatRate ?? 0,

    // Scope appendix
    scope,

    // Payment schedule
    paymentSchedule: schedule,

    // Contract type
    contractType,

    // Per-contract fields — defaults from CONTRACT_DEFAULTS (user fills before printing)
    contractNumber: meta.contractNumber ?? '',
    startDate: '',
    endDate: '',
    warrantyYears: CONTRACT_DEFAULTS.warrantyYears,
    penaltyPercentPerDay: CONTRACT_DEFAULTS.penaltyPercentPerDay,
    maxPenaltyPercent: CONTRACT_DEFAULTS.maxPenaltyPercent,
    acceptanceDays: CONTRACT_DEFAULTS.acceptanceDays,
    copiesCount: CONTRACT_DEFAULTS.copiesCount,
  };
}
